/**
 * Public Environment wire schemas (R-009).
 *
 * The UI consumes the Core identity and Environment schemas directly so the
 * wire surface cannot silently drift from the canonical domain shapes. Signed
 * projection schemas below add the request, membership, coverage, and cursor
 * algebra required at the HTTP boundary.
 */

import {
  ENVIRONMENT_ACTION_TYPE_REGISTRY_V1 as CORE_ENVIRONMENT_ACTION_TYPE_REGISTRY_V1,
  ENVIRONMENT_OPERATION_PRODUCT_CONTROLS_V1 as CORE_ENVIRONMENT_OPERATION_PRODUCT_CONTROLS_V1,
  environmentActionTypeIdSchema,
  environmentCapabilitiesSchema,
  environmentDigestSchema,
  environmentHealthSchema,
  environmentIdSchema,
  environmentOriginIdentitySchema,
  environmentSchema,
  environmentTenantAuthoritySchema,
  exactEnvironmentSubjectScopeSchema,
  publicKeyDescriptorSchema,
  resolvedEnvironmentIdentitySchema,
} from "@excellent/core/verification-environments";
import {
  ENVIRONMENT_REASON_REGISTRY_V1,
  type EnvironmentReasonCode,
} from "@excellent/core/verification-environments-reasons";
import { z } from "zod";
import type { Operation } from "../types";

export const ENVIRONMENT_WIRE_SCHEMA_VERSION = 1 as const;
export const ENVIRONMENT_ACTION_TYPE_REGISTRY_V1 = CORE_ENVIRONMENT_ACTION_TYPE_REGISTRY_V1;
export const ENVIRONMENT_OPERATION_PRODUCT_CONTROLS_V1 =
  CORE_ENVIRONMENT_OPERATION_PRODUCT_CONTROLS_V1;
const CANONICAL_OPERATION_KINDS_V1 = [
  "environment-enrollment",
  "environment-enrollment-batch",
  "environment-reconciliation",
  "environment-auto-enrollment-policy",
  "environment-action",
  "environment-rollout",
  "environment-rotation",
  "environment-generation-revocation",
  "environment-disconnection",
  "environment-removal",
  "environment-collision-resolution",
  "environment-local-authority-claim",
  "environment-offline-import",
  "source-promotion",
  "provider-connection",
  "provider-operation",
  "sandbox-pool-bootstrap",
  "sandbox-attachment",
  "sandbox-policy",
  "sandbox-finalization",
  "sandbox-incomplete-acknowledgement",
  "sandbox-detachment",
  "sandbox-promotion",
  "local-environment-genesis",
  "environment-invitation-issuance",
  "environment-invitation-redemption",
] as const;
const OPERATION_HANDLE_STATES_V1 = [
  "awaiting_decision",
  "cancelled_before_decision",
  "expired_before_decision",
  "rejected",
  "queued",
  "running",
  "stop_requested",
  "outcome_unknown",
  "retryable_failure",
  "failed",
  "partial_reconciling",
  "complete",
  "partial_terminal",
  "cancelled",
  "expired_after_decision",
] as const;
const OPERATION_NEXT_ACTION_CODES_V1 = [
  "wait",
  "review-plan",
  "resume-same-operation",
  "request-stop",
  "reconcile-same-operation",
  "create-new-plan",
  "resolve-collision",
  "repair-capability",
  "restore-connectivity",
  "reauthorize-read-only-reconciliation",
  "none",
] as const;

const id = z.string().min(1).max(160);
const digest = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const iso = z.string().datetime({ offset: true });
const nonNegativeInteger = z.number().int().nonnegative();
const positiveInteger = z.number().int().positive();

function sortCanonicalKeys(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(sortCanonicalKeys);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value as Record<string, unknown>).sort()) {
    out[key] = sortCanonicalKeys((value as Record<string, unknown>)[key]);
  }
  return out;
}

function rotateRight(value: number, shift: number): number {
  return (value >>> shift) | (value << (32 - shift));
}

function sha256Hex(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const bitLength = bytes.length * 8;
  const paddedLength = ((bytes.length + 9 + 63) >> 6) << 6;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 4, bitLength, false);
  const hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const constants = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ] as const;
  const words = new Uint32Array(64);
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4, false);
    }
    for (let index = 16; index < 64; index += 1) {
      const s0 =
        rotateRight(words[index - 15]!, 7) ^
        rotateRight(words[index - 15]!, 18) ^
        (words[index - 15]! >>> 3);
      const s1 =
        rotateRight(words[index - 2]!, 17) ^
        rotateRight(words[index - 2]!, 19) ^
        (words[index - 2]! >>> 10);
      words[index] = (words[index - 16]! + s0 + words[index - 7]! + s1) >>> 0;
    }
    let a = hash[0]!;
    let b = hash[1]!;
    let c = hash[2]!;
    let d = hash[3]!;
    let e = hash[4]!;
    let f = hash[5]!;
    let g = hash[6]!;
    let h = hash[7]!;
    for (let index = 0; index < 64; index += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + constants[index]! + words[index]!) >>> 0;
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    hash[0] = (hash[0]! + a) >>> 0;
    hash[1] = (hash[1]! + b) >>> 0;
    hash[2] = (hash[2]! + c) >>> 0;
    hash[3] = (hash[3]! + d) >>> 0;
    hash[4] = (hash[4]! + e) >>> 0;
    hash[5] = (hash[5]! + f) >>> 0;
    hash[6] = (hash[6]! + g) >>> 0;
    hash[7] = (hash[7]! + h) >>> 0;
  }
  return [...hash].map((part) => part.toString(16).padStart(8, "0")).join("");
}

function hashCanonicalBytes(value: unknown): string {
  return `sha256:${sha256Hex(JSON.stringify(sortCanonicalKeys(value)))}`;
}

export {
  environmentActionTypeIdSchema,
  environmentCapabilitiesSchema,
  environmentHealthSchema,
  environmentOriginIdentitySchema,
  environmentSchema,
  environmentTenantAuthoritySchema,
  exactEnvironmentSubjectScopeSchema,
  publicKeyDescriptorSchema,
  resolvedEnvironmentIdentitySchema,
};
export type Environment = z.infer<typeof environmentSchema>;
export type EnvironmentCapabilities = z.infer<typeof environmentCapabilitiesSchema>;
export type EnvironmentHealth = z.infer<typeof environmentHealthSchema>;
export type EnvironmentOriginIdentity = z.infer<typeof environmentOriginIdentitySchema>;
export type EnvironmentTenantAuthority = z.infer<typeof environmentTenantAuthoritySchema>;
export type ResolvedEnvironmentIdentity = z.infer<typeof resolvedEnvironmentIdentitySchema>;

export const environmentReasonCodeSchema = z.enum([...ENVIRONMENT_REASON_REGISTRY_V1] as [
  EnvironmentReasonCode,
  ...EnvironmentReasonCode[],
]);

export const environmentQueryScopeSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("environment"),
      environmentId: id,
      generationId: id,
    })
    .strict(),
  z
    .object({
      kind: z.literal("environment-set"),
      environmentIds: z.array(id).min(1),
      environmentSetDigest: digest,
    })
    .strict(),
]);
export type EnvironmentQueryScope = z.infer<typeof environmentQueryScopeSchema>;

export const resolvedEnvironmentScopeMemberSchema = z
  .object({
    environmentId: id,
    generationId: id,
    admittedResourceScopeDigest: digest,
  })
  .strict();
export type ResolvedEnvironmentScopeMember = z.infer<typeof resolvedEnvironmentScopeMemberSchema>;

export const environmentVerificationScopeSchema = z
  .object({
    kind: z.literal("resolved-environment-scope"),
    members: z.array(resolvedEnvironmentScopeMemberSchema).min(1),
    environmentScopeDigest: digest,
  })
  .strict();
export type EnvironmentVerificationScope = z.infer<typeof environmentVerificationScopeSchema>;

export const environmentPublicErrorSchema = z
  .object({
    schemaId: z.literal("excellent.environment.public-error.v1"),
    reasonCode: environmentReasonCodeSchema,
    authorityScope: z
      .discriminatedUnion("kind", [
        z.object({ kind: z.literal("tenant-directory"), tenantId: id }).strict(),
        z
          .object({
            kind: z.literal("environment"),
            tenantId: id,
            environmentId: id,
            generationId: id,
            authorityBindingDigest: digest,
          })
          .strict(),
      ])
      .nullable(),
    subjectId: id.nullable(),
    safeEndpoint: z.string().min(1).nullable(),
    lastSuccessfulStage: z.string().min(1).nullable(),
    preservedStateDigest: digest.nullable(),
    nextActionCode: z.string().min(1),
  })
  .strict();
export type EnvironmentPublicError = z.infer<typeof environmentPublicErrorSchema>;

export const environmentDirectoryReadSchema = z
  .object({
    environment: environmentSchema,
    resolvedIdentity: resolvedEnvironmentIdentitySchema,
  })
  .strict()
  .superRefine((read, context) => {
    const identity = read.resolvedIdentity;
    const environment = read.environment;
    const equalities: Array<[unknown, unknown, string]> = [
      [identity.originTenantId, environment.originTenantId, "originTenantId"],
      [identity.environmentId, environment.id, "environmentId"],
      [identity.generationId, environment.generationId, "generationId"],
      [identity.displayName, environment.displayName, "displayName"],
      [identity.kind, environment.kind, "kind"],
      [identity.topologyProfile, environment.topologyProfile, "topologyProfile"],
      [identity.createdAt, environment.createdAt, "createdAt"],
    ];
    for (const [identityValue, environmentValue, field] of equalities) {
      if (identityValue !== environmentValue) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["resolvedIdentity", field],
          message: `resolved identity ${field} must match the persisted Environment`,
        });
      }
    }
  });
export type EnvironmentDirectoryRead = z.infer<typeof environmentDirectoryReadSchema>;

export const environmentListQuerySchema = z.discriminatedUnion("scope", [
  z
    .object({
      scope: z.literal("environment"),
      environmentId: id,
      generationId: id,
      env: z.enum(["demo", "real"]).optional(),
    })
    .strict(),
  z
    .object({
      scope: z.literal("all"),
      env: z.enum(["demo", "real"]).optional(),
    })
    .strict(),
]);
export type EnvironmentListQuery = z.infer<typeof environmentListQuerySchema>;

const signedEnvironmentProjectionPayloadSchema = z
  .object({
    scope: exactEnvironmentSubjectScopeSchema,
    callerPrincipalDigest: digest,
    observationGrantDigest: digest,
    policyAndGrantEpochDigest: digest,
    endpointSetDigest: digest,
    querySemanticsDigest: digest,
    projectionFreshnessPolicyDigest: digest,
    aggregateFieldSchemaDigest: digest,
    projectionSequence: nonNegativeInteger,
    previousProjectionDigest: digest.nullable(),
    observedAt: iso,
    validUntil: iso,
    cursor: z.string().min(1),
    data: environmentDirectoryReadSchema,
  })
  .strict()
  .superRefine((payload, context) => {
    if (
      (payload.projectionSequence === 0 && payload.previousProjectionDigest !== null) ||
      (payload.projectionSequence > 0 && payload.previousProjectionDigest === null)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["previousProjectionDigest"],
        message: "projection sequence and predecessor digest must agree",
      });
    }
    if (
      payload.scope.environmentId !== payload.data.environment.id ||
      payload.scope.generationId !== payload.data.environment.generationId
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scope"],
        message: "signed projection scope must match the projected Environment generation",
      });
    }
    const authority = payload.data.resolvedIdentity.resolvedAuthority;
    const authorityTenantId =
      authority.authorityState === "local-origin"
        ? authority.originTenantId
        : authority.authorityTenantId;
    if (payload.scope.tenantId !== authorityTenantId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scope", "tenantId"],
        message: "signed projection tenant must match the resolved Environment authority",
      });
    }
  });

export const signedEnvironmentProjectionSchema = z
  .object({
    protocol: z.literal("excellent.environment-enrollment"),
    schemaVersion: z.literal(1),
    artifactType: z.literal("environment-projection"),
    signingDomain: z.literal("excellent.environment-projection.v1"),
    algorithm: z.string().min(1),
    signerKeyId: z.string().min(1),
    issuer: z.string().min(1),
    audience: z.literal("excellent.environment-projection-consumer"),
    issuedAt: iso,
    expiresAt: iso,
    nonce: z.string().min(1),
    payload: signedEnvironmentProjectionPayloadSchema,
    signature: z.string().min(1),
  })
  .strict()
  .superRefine((projection, context) => {
    if (projection.expiresAt !== projection.payload.validUntil) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expiresAt"],
        message: "projection envelope expiry must match its payload validity bound",
      });
    }
  });

export const environmentProjectionTransitionPayloadSchema = z
  .object({
    expectedHeadVersion: nonNegativeInteger,
    expectedHeadDigest: digest.nullable(),
    transitionSequence: nonNegativeInteger,
    previousTransitionDigest: digest.nullable(),
    projectionStreamId: id,
    scope: exactEnvironmentSubjectScopeSchema,
    callerPrincipalDigest: digest,
    observationGrantDigest: digest,
    policyAndGrantEpochDigest: digest,
    querySemanticsDigest: digest,
    projectionFreshnessPolicyDigest: digest,
    aggregateFieldSchemaDigest: digest,
    nextProjectionDigest: digest,
    signedProjection: signedEnvironmentProjectionSchema,
    recordedAt: iso,
  })
  .strict()
  .superRefine((transition, context) => {
    const genesis = transition.transitionSequence === 0;
    if (
      genesis !== (transition.previousTransitionDigest === null) ||
      (transition.expectedHeadVersion === 0) !== (transition.expectedHeadDigest === null) ||
      genesis !== (transition.expectedHeadVersion === 0)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "projection transition sequence, predecessor, and expected head must agree",
      });
    }
    if (
      transition.expectedHeadVersion > 0 &&
      transition.expectedHeadDigest !== transition.previousTransitionDigest
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expectedHeadDigest"],
        message: "successor transition must expect its exact predecessor head digest",
      });
    }
  });

export const signedEnvironmentProjectionTransitionSchema = z
  .object({
    protocol: z.literal("excellent.environment-enrollment"),
    schemaVersion: z.literal(1),
    artifactType: z.literal("environment-projection-transition"),
    signingDomain: z.literal("excellent.environment-projection.transition.v1"),
    algorithm: z.string().min(1),
    signerKeyId: z.string().min(1),
    issuer: z.string().min(1),
    audience: z.literal("excellent.environment-projection-consumer"),
    issuedAt: iso,
    expiresAt: iso,
    nonce: z.string().min(1),
    payload: environmentProjectionTransitionPayloadSchema,
    signature: z.string().min(1),
  })
  .strict();

export const environmentProjectionTransitionArtifactSchema = z
  .object({
    transitionDigest: digest,
    signedTransition: signedEnvironmentProjectionTransitionSchema,
  })
  .strict();

export const canonicalEnvironmentProjectionHeadSchema = z
  .object({
    scope: exactEnvironmentSubjectScopeSchema,
    callerPrincipalDigest: digest,
    observationGrantDigest: digest,
    policyAndGrantEpochDigest: digest,
    querySemanticsDigest: digest,
    aggregateFieldSchemaDigest: digest,
    headVersion: positiveInteger,
    headDigest: digest,
    currentProjectionSequence: nonNegativeInteger,
    currentProjectionDigest: digest,
    projectionStreamId: id,
    projectionFreshnessPolicyDigest: digest,
    currentTransitionDigest: digest,
  })
  .strict();

export const currentEnvironmentProjectionSchema = z
  .object({
    projectionDigest: digest,
    signedProjection: signedEnvironmentProjectionSchema,
    transition: environmentProjectionTransitionArtifactSchema,
    currentHead: canonicalEnvironmentProjectionHeadSchema,
  })
  .strict()
  .superRefine((current, context) => {
    const projection = current.signedProjection.payload;
    const transitionArtifact = current.transition;
    const signedTransition = transitionArtifact.signedTransition;
    const transition = signedTransition.payload;
    const head = current.currentHead;
    const expectedProjectionDigest = hashCanonicalBytes(current.signedProjection);
    const expectedTransitionDigest = hashCanonicalBytes(signedTransition);

    if (
      current.projectionDigest !== expectedProjectionDigest ||
      transition.nextProjectionDigest !== expectedProjectionDigest ||
      hashCanonicalBytes(transition.signedProjection) !== expectedProjectionDigest
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["projectionDigest"],
        message: "projection digest must bind the byte-identical signed projection",
      });
    }
    if (
      transitionArtifact.transitionDigest !== expectedTransitionDigest ||
      head.headDigest !== expectedTransitionDigest ||
      head.currentTransitionDigest !== expectedTransitionDigest
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["transition"],
        message: "transition artifact and current head must bind the signed transition",
      });
    }
    if (
      transition.expectedHeadVersion + 1 !== head.headVersion ||
      transition.transitionSequence !== projection.projectionSequence ||
      head.currentProjectionSequence !== projection.projectionSequence ||
      head.currentProjectionDigest !== expectedProjectionDigest
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["currentHead"],
        message: "projection transition must advance the exact CAS head and sequence",
      });
    }

    const scopeBytes = JSON.stringify(projection.scope);
    const bindingFields = [
      "callerPrincipalDigest",
      "observationGrantDigest",
      "policyAndGrantEpochDigest",
      "querySemanticsDigest",
      "projectionFreshnessPolicyDigest",
      "aggregateFieldSchemaDigest",
    ] as const;
    if (
      JSON.stringify(transition.scope) !== scopeBytes ||
      JSON.stringify(head.scope) !== scopeBytes ||
      transition.projectionStreamId !== head.projectionStreamId ||
      bindingFields.some(
        (field) => transition[field] !== projection[field] || head[field] !== projection[field],
      )
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["currentHead"],
        message: "projection, transition, and head authority/query bindings must be identical",
      });
    }
  });
export type CurrentEnvironmentProjection = z.infer<typeof currentEnvironmentProjectionSchema>;

const currentMemberReferenceShape = {
  memberReferenceKind: z.literal("current-identity"),
  formerMemberOpaqueRef: z.null(),
  frozenTupleToOpaqueSlotCommitmentDigest: z.null(),
};

const currentAvailableMemberCommonShape = {
  environmentId: id,
  generationId: id,
  observedAt: iso,
  cursor: z.string().min(1),
  data: environmentDirectoryReadSchema,
  currentProjection: currentEnvironmentProjectionSchema,
  ...currentMemberReferenceShape,
};

const freshCurrentProjectionMemberSchema = z
  .object({
    ...currentAvailableMemberCommonShape,
    state: z.literal("fresh"),
    reasonCode: z.null(),
    refreshContact: z.literal("responded"),
  })
  .strict();
const staleCurrentProjectionMemberSchema = z
  .object({
    ...currentAvailableMemberCommonShape,
    state: z.literal("stale"),
    reasonCode: environmentReasonCodeSchema,
    refreshContact: z.enum(["responded", "not-reached"]),
  })
  .strict();
const offlineCurrentProjectionMemberSchema = z
  .object({
    ...currentAvailableMemberCommonShape,
    state: z.literal("offline"),
    reasonCode: environmentReasonCodeSchema,
    refreshContact: z.literal("not-reached"),
  })
  .strict();
const errorCurrentProjectionMemberSchema = z
  .object({
    environmentId: id,
    generationId: id,
    state: z.literal("error"),
    observedAt: z.null(),
    cursor: z.null(),
    data: z.null(),
    currentProjection: z.null(),
    authorityBindingDigest: digest,
    reasonCode: environmentReasonCodeSchema,
    refreshContact: z.enum(["responded", "not-reached"]),
    missingDataConsequence: z.enum(["partial", "unknown", "cannot-recompute"]),
    ...currentMemberReferenceShape,
  })
  .strict();

export const currentEnvironmentProjectionMemberSchema = z
  .discriminatedUnion("state", [
    freshCurrentProjectionMemberSchema,
    staleCurrentProjectionMemberSchema,
    offlineCurrentProjectionMemberSchema,
    errorCurrentProjectionMemberSchema,
  ])
  .superRefine((member, context) => {
    if (member.state === "error") return;
    const projection = member.currentProjection.signedProjection.payload;
    if (
      member.environmentId !== projection.scope.environmentId ||
      member.generationId !== projection.scope.generationId ||
      member.observedAt !== projection.observedAt ||
      member.cursor !== projection.cursor ||
      JSON.stringify(member.data) !== JSON.stringify(projection.data)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "member identity, cursor, observation, and data must match its signed projection",
      });
    }
  });
export type CurrentEnvironmentProjectionMember = z.infer<
  typeof currentEnvironmentProjectionMemberSchema
>;

export const currentProjectionOmissionSchema = z
  .object({
    environmentId: id,
    generationId: id,
    authorityBindingDigest: digest,
    reasonCode: environmentReasonCodeSchema,
    missingDataConsequence: z.enum(["partial", "unknown", "cannot-recompute"]),
  })
  .strict();

export const currentProjectionCoverageSchema = z
  .object({
    requestedAuthorizedCount: nonNegativeInteger,
    contactedCount: nonNegativeInteger,
    freshCount: nonNegativeInteger,
    staleCount: nonNegativeInteger,
    unavailableCount: nonNegativeInteger,
    omitted: z.array(currentProjectionOmissionSchema),
  })
  .strict();

const membershipCommitmentCommonShape = {
  tenantId: id,
  membershipSequence: nonNegativeInteger,
  canonicalMemberReferenceSetDigest: digest,
  requestAndFilterSemanticsDigest: digest,
  projectionFreshnessPolicyDigest: digest,
  aggregateFieldSchemaDigest: digest,
  issuedAt: iso,
  directoryMembershipHeadVersion: positiveInteger,
  directoryMembershipHeadDigest: digest,
  callerPrincipalDigest: digest,
  observationGrantDigest: digest,
  policyAndGrantEpochDigest: digest,
  canonicalAuthorityBoundMemberSetDigest: digest,
  currentAuthorizationDigest: digest,
  authorizationSnapshotDigest: z.null(),
  frozenMembershipHeadDigest: z.null(),
};

export const currentMembershipCommitmentPayloadSchema = z
  .object({
    ...membershipCommitmentCommonShape,
    projectionKind: z.literal("current"),
    canonicalMemberReferenceCount: positiveInteger,
    directoryMemberCount: positiveInteger,
  })
  .strict()
  .superRefine((payload, context) => {
    if (payload.directoryMemberCount !== payload.canonicalMemberReferenceCount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["directoryMemberCount"],
        message: "directory and canonical member counts must agree",
      });
    }
  });

export const emptyCurrentMembershipCommitmentPayloadSchema = z
  .object({
    ...membershipCommitmentCommonShape,
    projectionKind: z.literal("current-empty"),
    canonicalMemberReferenceCount: z.literal(0),
    directoryMemberCount: z.literal(0),
  })
  .strict();

function signedMembershipCommitmentSchema<T extends z.ZodTypeAny>(payload: T) {
  return z
    .object({
      protocol: z.literal("excellent.environment-projection"),
      schemaVersion: z.literal(1),
      artifactType: z.literal("environment-set-membership-commitment"),
      signingDomain: z.literal("excellent.environment-projection.membership-commitment.v1"),
      algorithm: z.string().min(1),
      signerKeyId: z.string().min(1),
      issuer: z.string().min(1),
      audience: z.literal("excellent.environment-projection-verifier"),
      signerPurpose: z.literal("tenant-directory-membership-commitment"),
      expiresAt: z.null(),
      payload,
      signature: z.string().min(1),
    })
    .strict();
}

export const signedCurrentMembershipCommitmentSchema = signedMembershipCommitmentSchema(
  currentMembershipCommitmentPayloadSchema,
);
export const signedEmptyCurrentMembershipCommitmentSchema = signedMembershipCommitmentSchema(
  emptyCurrentMembershipCommitmentPayloadSchema,
);

function memberReferenceKey(member: { environmentId: string; generationId: string }): string {
  return `${member.environmentId}\u0000${member.generationId}`;
}

function compareCanonicalStrings(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

type CurrentMemberReference = {
  environmentId: string;
  generationId: string;
  authorityBindingDigest: string;
};

function currentMemberReference(
  member: z.infer<typeof currentEnvironmentProjectionMemberSchema>,
): CurrentMemberReference {
  return {
    environmentId: member.environmentId,
    generationId: member.generationId,
    authorityBindingDigest:
      member.state === "error"
        ? member.authorityBindingDigest
        : member.currentProjection.signedProjection.payload.scope.authorityBindingDigest,
  };
}

function canonicalCurrentMemberReferences(
  tenantId: string,
  members: z.infer<typeof currentEnvironmentProjectionMemberSchema>[],
  omitted: z.infer<typeof currentProjectionOmissionSchema>[],
) {
  return [
    ...members.map(currentMemberReference),
    ...omitted.map((member) => ({
      environmentId: member.environmentId,
      generationId: member.generationId,
      authorityBindingDigest: member.authorityBindingDigest,
    })),
  ]
    .sort((left, right) =>
      compareCanonicalStrings(memberReferenceKey(left), memberReferenceKey(right)),
    )
    .map((member) => ({
      tenantId,
      environmentId: member.environmentId,
      generationId: member.generationId,
      authorityBindingDigest: member.authorityBindingDigest,
    }));
}

export const currentEnvironmentSetProjectionPayloadSchema = z
  .object({
    projectionKind: z.literal("current"),
    environmentSetDigest: digest,
    currentAuthorizationDigest: digest,
    membershipCommitment: signedCurrentMembershipCommitmentSchema,
    generatedAt: iso,
    memberSlotCount: positiveInteger,
    memberSlotEquation: z.literal("memberSlotCount=members.length+coverage.omitted.length"),
    completeness: z.enum(["complete", "partial", "unknown"]),
    coverage: currentProjectionCoverageSchema,
    members: z.array(currentEnvironmentProjectionMemberSchema),
  })
  .strict()
  .superRefine((projection, context) => {
    const commitment = projection.membershipCommitment.payload;
    const slotCount = projection.members.length + projection.coverage.omitted.length;
    if (
      projection.memberSlotCount !== slotCount ||
      projection.coverage.requestedAuthorizedCount !== slotCount ||
      commitment.canonicalMemberReferenceCount !== slotCount
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["memberSlotCount"],
        message: "current projection denominator equations must agree exactly",
      });
    }
    if (projection.currentAuthorizationDigest !== commitment.currentAuthorizationDigest) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["currentAuthorizationDigest"],
        message: "projection and membership authorization digests must match",
      });
    }

    const authorityBoundReferences = canonicalCurrentMemberReferences(
      commitment.tenantId,
      projection.members,
      projection.coverage.omitted,
    );
    const memberReferences = authorityBoundReferences.map(
      ({ tenantId, environmentId, generationId }) => ({
        tenantId,
        environmentId,
        generationId,
      }),
    );
    if (
      commitment.canonicalMemberReferenceSetDigest !== hashCanonicalBytes(memberReferences) ||
      commitment.canonicalAuthorityBoundMemberSetDigest !==
        hashCanonicalBytes(authorityBoundReferences) ||
      projection.environmentSetDigest !== hashCanonicalBytes(projection.membershipCommitment)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["membershipCommitment"],
        message: "membership and authority digests must bind the exact canonical member rows",
      });
    }

    const memberKeys = projection.members.map(memberReferenceKey);
    const omittedKeys = projection.coverage.omitted.map(memberReferenceKey);
    const allKeys = [...memberKeys, ...omittedKeys];
    if (new Set(allKeys).size !== allKeys.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["members"],
        message: "member and omission references must be unique and disjoint",
      });
    }
    const environmentIds = [
      ...projection.members.map((member) => member.environmentId),
      ...projection.coverage.omitted.map((member) => member.environmentId),
    ];
    if (new Set(environmentIds).size !== environmentIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["members"],
        message: "one current projection cannot contain multiple slots for an Environment",
      });
    }

    const freshCount = projection.members.filter((member) => member.state === "fresh").length;
    const staleCount = projection.members.filter(
      (member) => member.state === "stale" || member.state === "offline",
    ).length;
    const unavailableCount =
      projection.members.filter((member) => member.state === "error").length +
      projection.coverage.omitted.length;
    const contactedCount = projection.members.filter(
      (member) => member.refreshContact === "responded",
    ).length;
    if (
      projection.coverage.freshCount !== freshCount ||
      projection.coverage.staleCount !== staleCount ||
      projection.coverage.unavailableCount !== unavailableCount ||
      projection.coverage.contactedCount !== contactedCount
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["coverage"],
        message: "coverage counts must be derived from the signed member and omission rows",
      });
    }

    const consequences = [
      ...projection.members.flatMap((member) =>
        member.state === "error" ? [member.missingDataConsequence] : [],
      ),
      ...projection.coverage.omitted.map((member) => member.missingDataConsequence),
    ];
    const expectedCompleteness = consequences.some(
      (value) => value === "unknown" || value === "cannot-recompute",
    )
      ? "unknown"
      : freshCount === projection.members.length && projection.coverage.omitted.length === 0
        ? "complete"
        : "partial";
    if (projection.completeness !== expectedCompleteness) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["completeness"],
        message: "completeness must follow the member states and missing-data consequences",
      });
    }

    for (const [index, member] of projection.members.entries()) {
      if (member.state === "error") continue;
      const payload = member.currentProjection.signedProjection.payload;
      if (
        payload.scope.tenantId !== commitment.tenantId ||
        payload.callerPrincipalDigest !== commitment.callerPrincipalDigest ||
        payload.observationGrantDigest !== commitment.observationGrantDigest ||
        payload.policyAndGrantEpochDigest !== commitment.policyAndGrantEpochDigest ||
        payload.querySemanticsDigest !== commitment.requestAndFilterSemanticsDigest ||
        payload.projectionFreshnessPolicyDigest !== commitment.projectionFreshnessPolicyDigest ||
        payload.aggregateFieldSchemaDigest !== commitment.aggregateFieldSchemaDigest
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["members", index, "currentProjection"],
          message: "member projection authority and query bindings must match membership",
        });
      }
    }
  });

export const emptyCurrentEnvironmentSetProjectionPayloadSchema = z
  .object({
    projectionKind: z.literal("current-empty"),
    environmentSetDigest: digest,
    currentAuthorizationDigest: digest,
    requestAndFilterSemanticsDigest: digest,
    membershipCommitment: signedEmptyCurrentMembershipCommitmentSchema,
    generatedAt: iso,
    memberSlotCount: z.literal(0),
    memberSlotEquation: z.literal("memberSlotCount=members.length+coverage.omitted.length"),
    completeness: z.literal("not-applicable"),
    emptyReason: z.enum([
      "no-authorized-environments",
      "filter-matched-no-authorized-environments",
    ]),
    members: z.tuple([]),
    omitted: z.tuple([]),
  })
  .strict()
  .superRefine((projection, context) => {
    const commitment = projection.membershipCommitment.payload;
    if (
      projection.currentAuthorizationDigest !== commitment.currentAuthorizationDigest ||
      projection.requestAndFilterSemanticsDigest !== commitment.requestAndFilterSemanticsDigest ||
      commitment.canonicalMemberReferenceSetDigest !== hashCanonicalBytes([]) ||
      commitment.canonicalAuthorityBoundMemberSetDigest !== hashCanonicalBytes([]) ||
      projection.environmentSetDigest !== hashCanonicalBytes(projection.membershipCommitment)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["membershipCommitment"],
        message: "empty projection authorization and request bindings must match membership",
      });
    }
  });

function signedEnvironmentSetProjectionSchema<T extends z.ZodTypeAny>(payload: T) {
  return z
    .object({
      protocol: z.literal("excellent.environment-enrollment"),
      schemaVersion: z.literal(1),
      artifactType: z.literal("environment-set-projection"),
      signingDomain: z.literal("excellent.environment-set-projection.coordinator.v1"),
      algorithm: z.string().min(1),
      signerKeyId: z.string().min(1),
      issuer: z.string().min(1),
      audience: z.literal("excellent.environment-projection-consumer"),
      issuedAt: iso,
      expiresAt: z.null(),
      nonce: z.string().min(1),
      payload,
      signature: z.string().min(1),
    })
    .strict();
}

export const signedCurrentEnvironmentSetProjectionSchema = signedEnvironmentSetProjectionSchema(
  currentEnvironmentSetProjectionPayloadSchema,
);
export const signedEmptyCurrentEnvironmentSetProjectionSchema =
  signedEnvironmentSetProjectionSchema(emptyCurrentEnvironmentSetProjectionPayloadSchema);

export const environmentListResponseSchema = z.union([
  signedCurrentEnvironmentSetProjectionSchema,
  signedEmptyCurrentEnvironmentSetProjectionSchema,
]);
export type EnvironmentListResponse = z.infer<typeof environmentListResponseSchema>;

/**
 * Request-scoped Environment presentation for the authenticated product shell.
 * This envelope deliberately does not reuse the legacy `demo | real` scope:
 * canonical identity is always the complete origin/Environment/generation tuple.
 */
export const environmentUiContextSubjectSchema = z
  .object({
    originTenantId: id,
    environmentId: id,
    generationId: id,
  })
  .strict();
export type EnvironmentUiContextSubject = z.infer<typeof environmentUiContextSubjectSchema>;

const environmentUiContextPageQueryShape = {
  cursor: z.string().min(1).max(4096).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
};

export const environmentUiContextQuerySchema = z.discriminatedUnion("scope", [
  z.object({ scope: z.literal("default"), ...environmentUiContextPageQueryShape }).strict(),
  z
    .object({
      scope: z.literal("environment"),
      originTenantId: id,
      environmentId: id,
      generationId: id,
      ...environmentUiContextPageQueryShape,
    })
    .strict(),
  z.object({ scope: z.literal("all"), ...environmentUiContextPageQueryShape }).strict(),
]);
export type EnvironmentUiContextQuery = z.infer<typeof environmentUiContextQuerySchema>;

export const environmentUiContextDirectorySummarySchema = z
  .object({
    subject: environmentUiContextSubjectSchema,
    displayName: z.string().min(1).max(320),
    kind: z.enum(["device", "server", "cluster", "service"]),
    topologyProfile: z.enum([
      "host",
      "fleet",
      "ci-pool",
      "sandbox-pool",
      "cluster",
      "replicated-service",
      "service",
    ]),
    stageLabel: z.enum(["development", "sandbox", "staging", "production"]).nullable(),
    dataAuthority: z.enum(["resident", "delegated", "offline"]),
    lifecycleState: z.enum([
      "reserved",
      "enrolling",
      "active",
      "retiring",
      "quarantined",
      "revoked",
      "removed",
    ]),
    trustState: z.enum([
      "unverified",
      "pinned",
      "attested",
      "rotation_due",
      "trust_failed",
      "revoked",
    ]),
    lastContactAt: iso.nullable(),
    lastProjectionAt: iso.nullable(),
  })
  .strict();

const environmentUiContextDirectoryPageSchema = z
  .object({
    items: z.array(environmentUiContextDirectorySummarySchema).max(50),
    pageSize: z.number().int().min(1).max(50),
    nextCursor: z.string().min(1).max(4096).nullable(),
    completeness: z.enum(["complete", "partial", "unknown"]),
    omittedCount: nonNegativeInteger.nullable(),
  })
  .strict();

/** Opaque authority binding for paged directory reads without a selected
 * Environment presentation. These values are references, not credentials. */
export const environmentUiContextDirectoryAuthoritySchema = z
  .object({
    authorityBindingDigest: digest,
    membershipCommitmentDigest: digest,
    grantEpochDigest: digest,
  })
  .strict();
export type EnvironmentUiContextDirectoryAuthority = z.infer<
  typeof environmentUiContextDirectoryAuthoritySchema
>;

export const environmentUiContextLocalitySchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("local"),
      reason: z.literal("verified-client-host-match"),
      evidenceDigests: z.array(digest).min(2),
    })
    .strict(),
  z
    .object({
      kind: z.literal("remote"),
      reason: z.enum(["verified-distinct-host", "verified-external-boundary"]),
      evidenceDigests: z.array(digest).min(2),
    })
    .strict(),
  z
    .object({
      kind: z.literal("offline-snapshot"),
      reason: z.literal("verified-immutable-import"),
      evidenceDigests: z.array(digest).min(3),
      capturedAt: iso,
    })
    .strict(),
  z
    .object({
      kind: z.literal("unknown"),
      reason: z.enum([
        "viewer-binding-unconfirmed",
        "placement-unconfirmed",
        "offline-linkage-unconfirmed",
        "environment-not-active",
      ]),
      evidenceDigests: z.tuple([]),
    })
    .strict(),
]);

export const environmentUiContextPresentationSchema = z
  .object({
    schemaVersion: z.literal(1),
    subject: environmentUiContextSubjectSchema,
    authority: z
      .object({
        authorityTenantId: id,
        bindingDigest: digest,
        callerEpochDigest: digest,
        grantEpochDigest: digest,
        evidenceDigest: digest,
        validUntil: iso,
      })
      .strict(),
    viewerBinding: z.discriminatedUnion("state", [
      z.object({ state: z.literal("verified"), evidenceDigest: digest, validUntil: iso }).strict(),
      z.object({ state: z.literal("unknown") }).strict(),
    ]),
    servingNode: z.discriminatedUnion("state", [
      z
        .object({
          state: z.literal("verified"),
          nodeId: id,
          evidenceDigest: digest,
          validUntil: iso,
        })
        .strict(),
      z.object({ state: z.literal("unknown") }).strict(),
    ]),
    locality: environmentUiContextLocalitySchema,
    displayName: z.string().min(1).max(320),
    displayLabel: z.string().min(1).max(320),
    evaluatedAt: iso,
    validUntil: iso,
    provenance: z
      .object({
        projectionDigest: digest,
        interpretation: z.enum(["current", "historical"]),
      })
      .strict(),
  })
  .strict();
export type EnvironmentUiContextPresentation = z.infer<
  typeof environmentUiContextPresentationSchema
>;

const environmentUiContextHealthEvaluationSchema = z
  .object({
    evaluatedAt: iso,
    validUntil: iso,
    policyDigest: digest,
    verificationEvidenceDigest: digest,
    reasonCodes: z.array(z.string().min(1)),
    health: environmentHealthSchema,
  })
  .strict();

const environmentUiContextCapabilityEvaluationSchema = z
  .object({
    evaluatedAt: iso,
    validUntil: iso,
    inventoryDigest: digest,
    policyDigest: digest,
    authorityDigest: digest,
    verificationEvidenceDigest: digest,
    reasonCodes: z.array(z.string().min(1)),
    capabilities: environmentCapabilitiesSchema,
    capabilitiesDigest: digest,
  })
  .strict();

const environmentUiContextNodeSubjectSchema = z
  .object({
    nodeId: id,
    nodeIdentityDigest: digest,
    relationship: z.enum(["serving", "member"]),
  })
  .strict();

const environmentUiContextNodeSyncSchema = z.discriminatedUnion("attributionState", [
  z
    .object({
      attributionState: z.literal("unknown"),
      reasonCode: z.enum([
        "node-sync-observation-unavailable",
        "node-sync-observer-unattributed",
        "node-sync-observation-mismatched",
        "node-sync-observation-expired",
      ]),
      subjectNode: environmentUiContextNodeSubjectSchema.nullable(),
      evaluatedAt: iso,
    })
    .strict(),
  z
    .object({
      attributionState: z.literal("attributed"),
      subjectNode: environmentUiContextNodeSubjectSchema,
      observerNode: z.object({ nodeId: id, nodeIdentityDigest: digest }).strict(),
      measurement: z
        .object({
          subjectNodeId: id,
          observerNodeId: id,
          plane: z.enum(["solo", "team"]),
          storageMode: z.enum(["plain-local", "embedded-replica", "team-crr"]),
          connectionState: z.enum(["single-player", "connected", "degraded", "offline"]),
          observedAt: iso,
          validUntil: iso,
          lastSuccessfulSyncAt: iso.nullable(),
          evidenceDigest: digest,
        })
        .strict(),
      evaluatedAt: iso,
      validUntil: iso,
    })
    .strict(),
]);

export const environmentUiContextOperationalStatusSchema = z
  .object({
    schemaId: z.literal("excellent.environment.operational-status.v1"),
    originTenantId: id,
    environmentId: id,
    generationId: id,
    evaluatedAt: iso,
    validUntil: iso,
    health: environmentUiContextHealthEvaluationSchema,
    capabilities: environmentUiContextCapabilityEvaluationSchema,
    nodeSync: environmentUiContextNodeSyncSchema,
    canonicalDigest: digest,
  })
  .strict();

export const environmentUiContextOperationalStateSchema = z.discriminatedUnion("state", [
  z
    .object({ state: z.literal("current"), data: environmentUiContextOperationalStatusSchema })
    .strict(),
  z
    .object({
      state: z.literal("unknown"),
      reasonCode: z.enum([
        "observation-source-unavailable",
        "observation-unavailable",
        "observation-identity-mismatch",
        "observation-invalid",
        "observation-expired",
        "capability-digest-mismatch",
      ]),
      evaluatedAt: iso,
    })
    .strict(),
  z
    .object({
      state: z.literal("blocked"),
      reasonCode: z.literal("current-authority-required"),
      evaluatedAt: iso,
    })
    .strict(),
  z
    .object({
      state: z.literal("error"),
      reasonCode: z.literal("operational-status-unavailable"),
      evaluatedAt: iso,
    })
    .strict(),
]);

const environmentUiContextSelectionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("environment"), subject: environmentUiContextSubjectSchema }).strict(),
  z
    .object({
      kind: z.literal("environment-set"),
      environmentSetDigest: digest,
      memberCount: nonNegativeInteger,
    })
    .strict(),
]);

const environmentUiContextSetupSchema = z
  .object({
    rootState: z.enum([
      "owner-required",
      "inactive",
      "pending",
      "active",
      "disabled",
      "unresolved",
    ]),
    missingProvisions: z.array(
      z.enum([
        "independently-anchored-enrollment-receipt-and-log-evidence",
        "signed-current-environment-set-projection",
        "current-generation-resource-and-action-grant",
        "host-current-authority-resolver-with-final-use-recheck",
      ]),
    ),
    nextHref: z.literal("/settings/integrations"),
  })
  .strict();

export const environmentUiContextResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.ui-context.v1"),
    schemaVersion: z.literal(1),
    status: z.enum([
      "ready",
      "location-unknown",
      "environment-set",
      "no-selection",
      "setup-required",
      "error",
    ]),
    reasonCode: z.enum([
      "context-current",
      "locality-unconfirmed",
      "environment-set-current",
      "selection-required",
      "no-authorized-environments",
      "current-authority-provisioning-required",
      "current-authority-source-unavailable",
      "environment-not-found-or-inaccessible",
      "context-resolution-failed",
    ]),
    nextActionCode: z.enum([
      "none",
      "select-environment",
      "provision-current-environment-authority",
      "retry-context-resolution",
    ]),
    evaluatedAt: iso,
    validUntil: iso.nullable(),
    requestScope: z.enum(["default", "environment", "all"]),
    selection: environmentUiContextSelectionSchema.nullable(),
    directory: environmentUiContextDirectoryPageSchema,
    directoryAuthority: environmentUiContextDirectoryAuthoritySchema.nullable(),
    presentation: environmentUiContextPresentationSchema.nullable(),
    operationalStatus: environmentUiContextOperationalStateSchema,
    setup: environmentUiContextSetupSchema.nullable(),
  })
  .strict()
  .superRefine((context, refinement) => {
    const selectedEnvironment = context.selection?.kind === "environment";
    if (
      (context.status === "ready" || context.status === "location-unknown") &&
      (!selectedEnvironment || context.presentation === null || context.setup !== null)
    ) {
      refinement.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["status"],
        message: "selected context states require an authorized presentation and no setup payload",
      });
    }
    if (
      context.status === "setup-required" &&
      (context.selection !== null ||
        context.presentation !== null ||
        context.setup === null ||
        context.directory.items.length !== 0 ||
        context.operationalStatus.state !== "blocked")
    ) {
      refinement.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["status"],
        message: "setup state must not disclose Environment identity or operational data",
      });
    }
    if (
      context.presentation === null &&
      context.directory.items.length > 0 &&
      context.directoryAuthority === null
    ) {
      refinement.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["directoryAuthority"],
        message: "directory-only pages require an opaque current authority binding",
      });
    }
    if (
      (context.status === "ready" ||
        context.status === "location-unknown" ||
        context.status === "environment-set") &&
      (context.validUntil === null ||
        Date.parse(context.validUntil) <= Date.parse(context.evaluatedAt))
    ) {
      refinement.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["validUntil"],
        message: "authorized context states require a future response expiry",
      });
    }
    if (
      context.status === "environment-set" &&
      (context.selection?.kind !== "environment-set" || context.presentation !== null)
    ) {
      refinement.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["selection"],
        message:
          "set context requires an explicit set identity and no single-Environment presentation",
      });
    }
    if (context.presentation && context.selection?.kind === "environment") {
      const left = context.presentation.subject;
      const right = context.selection.subject;
      if (
        left.originTenantId !== right.originTenantId ||
        left.environmentId !== right.environmentId ||
        left.generationId !== right.generationId
      ) {
        refinement.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["presentation", "subject"],
          message: "presentation subject must equal the selected canonical subject",
        });
      }
    }
  });
export type EnvironmentUiContextResponse = z.infer<typeof environmentUiContextResponseSchema>;

const environmentIdParamsSchema = z.object({ environmentId: id }).strict();

export const environmentViewerChallengeBodySchema = z
  .object({ subject: environmentUiContextSubjectSchema })
  .strict();
export type EnvironmentViewerChallengeBody = z.infer<
  typeof environmentViewerChallengeBodySchema
>;

export const environmentViewerChallengeResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.viewer-challenge-response.v1"),
    challengeBytes: z.string().min(1).max(20_000),
    challengeDigest: digest,
    expiresAt: iso,
  })
  .strict();
export type EnvironmentViewerChallengeResponse = z.infer<
  typeof environmentViewerChallengeResponseSchema
>;

export const environmentViewerBindingBodySchema = z
  .object({
    challengeBytes: z.string().min(1).max(20_000),
    signatureBase64Url: z.string().min(1).max(256),
  })
  .strict();
export type EnvironmentViewerBindingBody = z.infer<
  typeof environmentViewerBindingBodySchema
>;

export const environmentViewerBindingResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.viewer-binding-response.v1"),
    bindingDigest: digest,
    validUntil: iso,
  })
  .strict();
export type EnvironmentViewerBindingResponse = z.infer<
  typeof environmentViewerBindingResponseSchema
>;

export const environmentGetResponseSchema = environmentDirectoryReadSchema;
export type EnvironmentGetResponse = z.infer<typeof environmentGetResponseSchema>;

export const environmentHealthResponseSchema = z
  .object({
    environmentId: id,
    generationId: id,
    health: environmentHealthSchema,
  })
  .strict();
export type EnvironmentHealthResponse = z.infer<typeof environmentHealthResponseSchema>;

export const environmentCapabilitiesResponseSchema = z
  .object({
    environmentId: id,
    generationId: id,
    capabilities: environmentCapabilitiesSchema,
    capabilitiesDigest: digest,
  })
  .strict();
export type EnvironmentCapabilitiesResponse = z.infer<typeof environmentCapabilitiesResponseSchema>;

const listOp: Operation<void, EnvironmentListQuery, void, EnvironmentListResponse> = {
  method: "GET",
  path: () => "/api/environments",
  query: environmentListQuerySchema,
  response: environmentListResponseSchema,
};

const uiContextOp: Operation<void, EnvironmentUiContextQuery, void, EnvironmentUiContextResponse> =
  {
    method: "GET",
    path: () => "/api/environments/ui-context",
    query: environmentUiContextQuerySchema,
    response: environmentUiContextResponseSchema,
  };

const viewerChallengeOp: Operation<
  void,
  void,
  EnvironmentViewerChallengeBody,
  EnvironmentViewerChallengeResponse
> = {
  method: "POST",
  path: () => "/api/environments/viewer-challenge",
  body: environmentViewerChallengeBodySchema,
  response: environmentViewerChallengeResponseSchema,
};

const viewerBindingOp: Operation<
  void,
  void,
  EnvironmentViewerBindingBody,
  EnvironmentViewerBindingResponse
> = {
  method: "POST",
  path: () => "/api/environments/viewer-binding",
  body: environmentViewerBindingBodySchema,
  response: environmentViewerBindingResponseSchema,
};

const getOp: Operation<{ environmentId: string }, void, void, EnvironmentGetResponse> = {
  method: "GET",
  path: ({ environmentId }) => `/api/environments/${encodeURIComponent(environmentId)}`,
  params: environmentIdParamsSchema,
  response: environmentGetResponseSchema,
};

const healthOp: Operation<{ environmentId: string }, void, void, EnvironmentHealthResponse> = {
  method: "GET",
  path: ({ environmentId }) => `/api/environments/${encodeURIComponent(environmentId)}/health`,
  params: environmentIdParamsSchema,
  response: environmentHealthResponseSchema,
};

const capabilitiesOp: Operation<
  { environmentId: string },
  void,
  void,
  EnvironmentCapabilitiesResponse
> = {
  method: "GET",
  path: ({ environmentId }) =>
    `/api/environments/${encodeURIComponent(environmentId)}/capabilities`,
  params: environmentIdParamsSchema,
  response: environmentCapabilitiesResponseSchema,
};

const operationIdParamsSchema = z.object({ operationId: id }).strict();
const idempotencyKeyBodySchema = z
  .object({
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();

export const environmentOperationKindSchema = z.enum(CANONICAL_OPERATION_KINDS_V1);
export const environmentOperationHandleStateSchema = z.enum(OPERATION_HANDLE_STATES_V1);
export const environmentOperationNextActionCodeSchema = z.enum(OPERATION_NEXT_ACTION_CODES_V1);

const publicOperationAuthorityScopeSchema = z
  .object({
    kind: z.string().min(1),
  })
  .passthrough();

export const environmentOperationHandleResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.operation-handle.v1"),
    operationId: environmentDigestSchema,
    tenantId: environmentIdSchema,
    operationKind: environmentOperationKindSchema,
    state: environmentOperationHandleStateSchema,
    planDigest: environmentDigestSchema,
    authorityScope: publicOperationAuthorityScopeSchema,
    authorityScopeDigest: environmentDigestSchema,
    plannedScopeDigest: environmentDigestSchema,
    authorizedScopeDigest: environmentDigestSchema.nullable(),
    decisionDigest: environmentDigestSchema.nullable(),
    lastCheckpointDigest: environmentDigestSchema.nullable(),
    irreversibleCheckpointDigest: environmentDigestSchema.nullable(),
    preservedEffectSetDigest: environmentDigestSchema,
    safeCancelState: z.enum(["available", "cannot_cancel", "stop_requested"]),
    resumeEligibility: z.enum([
      "already-active",
      "same-operation",
      "reconcile-first",
      "not-authorized",
      "settling",
      "new-plan-required",
      "terminal",
    ]),
    reconciliationRequired: z.boolean(),
    unresolvedReceiptDigest: environmentDigestSchema.nullable(),
    terminalReceiptDigest: environmentDigestSchema.nullable(),
    nextActionCode: environmentOperationNextActionCodeSchema,
    canonicalBytes: z.string().min(1),
    canonicalDigest: environmentDigestSchema,
  })
  .strict();
export type EnvironmentOperationHandleResponse = z.infer<
  typeof environmentOperationHandleResponseSchema
>;

export const environmentOperationEventSchema = z
  .object({
    schemaId: z.literal("excellent.environment.operation-event.v1"),
    operationId: environmentDigestSchema,
    sequence: nonNegativeInteger,
    eventType: z.string().min(1),
    state: environmentOperationHandleStateSchema,
    canonicalBytes: z.string().min(1),
    canonicalDigest: environmentDigestSchema,
  })
  .strict();
export type EnvironmentOperationEvent = z.infer<typeof environmentOperationEventSchema>;

export const environmentOperationEventsResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.operation-events.v1"),
    operationId: environmentDigestSchema,
    events: z.array(environmentOperationEventSchema),
    canonicalBytes: z.string().min(1),
    canonicalDigest: environmentDigestSchema,
  })
  .strict();
export type EnvironmentOperationEventsResponse = z.infer<
  typeof environmentOperationEventsResponseSchema
>;

export const environmentOperationCancelBodySchema = idempotencyKeyBodySchema.extend({
  reason: z.string().min(1).max(400).optional(),
  remainingStageDisposition: z
    .enum([
      "not-started",
      "safely-stoppable",
      "in-flight-external-write",
      "no-safely-stoppable-stage",
      "post-authority-fence",
    ])
    .optional(),
  successfulTargetSetDigest: digest.optional(),
  failedTargetSetDigest: digest.optional(),
  unknownTargetSetDigest: digest.optional(),
  notAttemptedTargetSetDigest: digest.optional(),
});
export type EnvironmentOperationCancelBody = z.infer<typeof environmentOperationCancelBodySchema>;

export const environmentOperationResumeBodySchema = idempotencyKeyBodySchema.extend({
  originalExecutionIdempotencyKey: z.string().min(1).max(200).optional(),
});
export type EnvironmentOperationResumeBody = z.infer<typeof environmentOperationResumeBodySchema>;

export const environmentOperationReconcileBodySchema = idempotencyKeyBodySchema.extend({
  authorizationId: id,
  canonicalSignedAuthorizationBytes: z.string().min(1),
  canonicalSignedTransitionBytes: z.string().min(1),
  canonicalSignedTerminalEffectBytes: z.string().min(1).nullable().optional(),
  canonicalSignedNextUnresolvedOutcomeBytes: z.string().min(1).nullable().optional(),
});
export type EnvironmentOperationReconcileBody = z.infer<
  typeof environmentOperationReconcileBodySchema
>;

export const providerConnectionIdParamsSchema = z
  .object({
    connectionId: id,
  })
  .strict();
export const providerConnectionPlanIdParamsSchema = z
  .object({
    planId: id,
  })
  .strict();

export const providerConnectionLifecycleBodySchema = z
  .object({
    idempotencyKey: z.string().min(8).max(160).optional(),
    provider: z.string().min(1).max(80).optional(),
    reason: z.string().min(1).max(400).optional(),
    decisionId: id.optional(),
    approvalDecisionDigest: digest.optional(),
    canonicalSignedProviderConnectionPlanBytes: z.string().min(1).optional(),
    canonicalSignedApprovalDecisionBytes: z.string().min(1).optional(),
    canonicalSignedProviderEvidenceBytes: z.string().min(1).optional(),
  })
  .strict();
export type ProviderConnectionLifecycleBody = z.infer<typeof providerConnectionLifecycleBodySchema>;

export const providerConnectionExpiredClaimRefusalBodySchema = z
  .object({
    idempotencyKey: z.string().min(8).max(160),
    providerCapabilityClaimDigest: digest,
    reason: z.string().min(1).max(400).optional(),
  })
  .strict();
export type ProviderConnectionExpiredClaimRefusalBody = z.infer<
  typeof providerConnectionExpiredClaimRefusalBodySchema
>;

export const providerConnectionLifecycleResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.provider-connection.lifecycle-surface.v1"),
    surface: z.string().min(1),
    operation: z.string().min(1),
    providerConnectionId: id.nullable(),
    planId: id.nullable(),
    lifecycleMode: z.enum(["custom", "deterministic-local"]),
    lifecycleOutcome: z.enum([
      "provider-connections.listed",
      "provider-connection.detail-read",
      "provider-connection.authorization-planned",
      "provider-connection.plan-executed",
      "provider-connection.reauthorization-planned",
      "provider-connection.revocation-planned",
      "provider-connection.inventory-refreshed",
      "provider-connection.expired-claim-refused",
      "provider-connection.custom-transcript",
    ]),
    hostBindingDigest: digest,
    provider: z.string().min(1).max(80).nullable(),
    connectionIds: z.array(id),
    inventoryDigest: digest.nullable(),
    attemptedProviderCapabilityClaimDigest: digest.nullable(),
    lifecycleDigest: digest,
    planDigest: digest.nullable(),
    decisionId: id.nullable(),
    approvalDecisionDigest: digest.nullable(),
    providerEvidenceDigest: digest.nullable(),
    providerConnectionReceiptDigest: digest.nullable(),
    canonicalSignedProviderConnectionPlanBytes: z.string().min(1).nullable(),
    canonicalSignedApprovalDecisionBytes: z.string().min(1).nullable(),
    canonicalSignedProviderEvidenceBytes: z.string().min(1).nullable(),
    canonicalSignedProviderConnectionReceiptBytes: z.string().min(1).nullable(),
    canonicalBytes: z.string().min(1),
    canonicalDigest: digest,
  })
  .strict();
export type ProviderConnectionLifecycleResponse = z.infer<
  typeof providerConnectionLifecycleResponseSchema
>;

export const environmentCurrentResourceGrantStatusResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.current-resource-grant.status.v1"),
    surface: z.literal("environment.current-resource-grant.status"),
    environmentId: id,
    generationId: id,
    grantState: z.enum(["effective", "unavailable"]),
    resourceGrantDigest: digest.nullable(),
    currentAuthorityDigest: digest.nullable(),
    allowedResourceRoots: z.array(z.string().min(1)),
    allowedActions: z.array(z.string().min(1)),
    leaseExpiresAt: iso.nullable(),
    validationTime: iso,
    canonicalBytes: z.string().min(1),
    canonicalDigest: digest,
  })
  .strict();
export type EnvironmentCurrentResourceGrantStatusResponse = z.infer<
  typeof environmentCurrentResourceGrantStatusResponseSchema
>;

export const environmentCollectorHostStatusResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.collector-host.status.v1"),
    surface: z.literal("environment.collector-host.status"),
    environmentId: id,
    generationId: id,
    collectorState: z.enum(["bound", "unavailable"]),
    collectorHostDigest: digest.nullable(),
    observationBoundary: z.string().min(1).nullable(),
    parentEvidenceDestinationDigest: digest.nullable(),
    spoolLimitBytes: positiveInteger.nullable(),
    checkpointIntervalSeconds: positiveInteger.nullable(),
    retentionDays: positiveInteger.nullable(),
    validationTime: iso,
    canonicalBytes: z.string().min(1),
    canonicalDigest: digest,
  })
  .strict();
export type EnvironmentCollectorHostStatusResponse = z.infer<
  typeof environmentCollectorHostStatusResponseSchema
>;

const environmentD1AdmissionCheckSchema = z
  .object({
    id: z.enum(["H02", "H03", "H04", "H14", "mcp-setup", "agent-observation", "task-context"]),
    label: z.string().min(1),
    status: z.enum(["pass", "blocked", "unresolved", "observed"]),
    evidence: z.string().min(1),
    nextAction: z.string().min(1),
  })
  .strict();
export type EnvironmentD1AdmissionCheck = z.infer<typeof environmentD1AdmissionCheckSchema>;

export const environmentD1AdmissionResponseSchema = z
  .object({
    schemaId: z.literal("excellent.environment.d1-admission.snapshot.v1"),
    surface: z.literal("environment.d1-admission"),
    kickoffSha: z.string().min(7).max(80),
    generatedAt: iso,
    account: z
      .object({
        state: z.enum(["authenticated", "anonymous", "needs-workspace", "blocked"]),
        workspaceId: z.string().nullable(),
        actorId: z.string().nullable(),
        authorityBindingDigest: digest.nullable(),
      })
      .strict(),
    repository: z
      .object({
        state: z.enum(["connected", "missing", "unresolved", "blocked"]),
        totalProjects: nonNegativeInteger,
        connectedRepositories: nonNegativeInteger,
        examples: z.array(z.string().min(1)).max(5),
      })
      .strict(),
    environment: z
      .object({
        state: z.enum(["current", "empty", "unresolved", "blocked"]),
        memberCount: nonNegativeInteger,
        reason: z.string().min(1).nullable(),
      })
      .strict(),
    observation: z
      .object({
        state: z.enum(["observed", "missing", "unresolved", "blocked"]),
        runningAgentRuns: nonNegativeInteger,
        recentAgentRuns: nonNegativeInteger,
        inProgressTasks: nonNegativeInteger,
        reviewTasks: nonNegativeInteger,
      })
      .strict(),
    mcpSetup: z
      .object({
        state: z.enum(["linked", "installable", "missing", "blocked"]),
        installedClis: nonNegativeInteger,
        supportedClis: nonNegativeInteger,
        linkedClis: nonNegativeInteger,
        installedLabels: z.array(z.string().min(1)).max(5),
        linkedLabels: z.array(z.string().min(1)).max(5),
        nextHref: z.literal("/settings/agents/clis"),
      })
      .strict(),
    checks: z.array(environmentD1AdmissionCheckSchema).min(1),
  })
  .strict();
export type EnvironmentD1AdmissionResponse = z.infer<typeof environmentD1AdmissionResponseSchema>;

const environmentCurrentResourceGrantStatusOp: Operation<
  { environmentId: string },
  void,
  void,
  EnvironmentCurrentResourceGrantStatusResponse
> = {
  method: "GET",
  path: ({ environmentId }) =>
    `/api/environments/${encodeURIComponent(environmentId)}/resource-grant-status`,
  params: environmentIdParamsSchema,
  response: environmentCurrentResourceGrantStatusResponseSchema,
};

const environmentCollectorHostStatusOp: Operation<
  { environmentId: string },
  void,
  void,
  EnvironmentCollectorHostStatusResponse
> = {
  method: "GET",
  path: ({ environmentId }) =>
    `/api/environments/${encodeURIComponent(environmentId)}/collector-host-status`,
  params: environmentIdParamsSchema,
  response: environmentCollectorHostStatusResponseSchema,
};

const environmentD1AdmissionOp: Operation<void, void, void, EnvironmentD1AdmissionResponse> = {
  method: "GET",
  path: () => "/api/environments/d1-admission",
  response: environmentD1AdmissionResponseSchema,
};

const operationStatusOp: Operation<
  { operationId: string },
  void,
  void,
  EnvironmentOperationHandleResponse
> = {
  method: "GET",
  path: ({ operationId }) => `/api/operations/${encodeURIComponent(operationId)}`,
  params: operationIdParamsSchema,
  response: environmentOperationHandleResponseSchema,
};

const operationEventsOp: Operation<
  { operationId: string },
  void,
  void,
  EnvironmentOperationEventsResponse
> = {
  method: "GET",
  path: ({ operationId }) => `/api/operations/${encodeURIComponent(operationId)}/events`,
  params: operationIdParamsSchema,
  response: environmentOperationEventsResponseSchema,
};

const operationCancelOp: Operation<
  { operationId: string },
  void,
  EnvironmentOperationCancelBody,
  EnvironmentOperationHandleResponse
> = {
  method: "POST",
  path: ({ operationId }) => `/api/operations/${encodeURIComponent(operationId)}/cancel`,
  params: operationIdParamsSchema,
  body: environmentOperationCancelBodySchema,
  response: environmentOperationHandleResponseSchema,
};

const operationResumeOp: Operation<
  { operationId: string },
  void,
  EnvironmentOperationResumeBody,
  EnvironmentOperationHandleResponse
> = {
  method: "POST",
  path: ({ operationId }) => `/api/operations/${encodeURIComponent(operationId)}/resume`,
  params: operationIdParamsSchema,
  body: environmentOperationResumeBodySchema,
  response: environmentOperationHandleResponseSchema,
};

const operationReconcileOp: Operation<
  { operationId: string },
  void,
  EnvironmentOperationReconcileBody,
  EnvironmentOperationHandleResponse
> = {
  method: "POST",
  path: ({ operationId }) => `/api/operations/${encodeURIComponent(operationId)}/reconcile`,
  params: operationIdParamsSchema,
  body: environmentOperationReconcileBodySchema,
  response: environmentOperationHandleResponseSchema,
};

const providerConnectionsListOp: Operation<void, void, void, ProviderConnectionLifecycleResponse> =
  {
    method: "GET",
    path: () => "/api/provider-connections",
    response: providerConnectionLifecycleResponseSchema,
  };

const providerConnectionAuthorizationPlanOp: Operation<
  void,
  void,
  ProviderConnectionLifecycleBody,
  ProviderConnectionLifecycleResponse
> = {
  method: "POST",
  path: () => "/api/provider-connections/authorization-plans",
  body: providerConnectionLifecycleBodySchema,
  response: providerConnectionLifecycleResponseSchema,
};

const providerConnectionPlanExecuteOp: Operation<
  { planId: string },
  void,
  ProviderConnectionLifecycleBody,
  ProviderConnectionLifecycleResponse
> = {
  method: "POST",
  path: ({ planId }) => `/api/provider-connection-plans/${encodeURIComponent(planId)}/execute`,
  params: providerConnectionPlanIdParamsSchema,
  body: providerConnectionLifecycleBodySchema,
  response: providerConnectionLifecycleResponseSchema,
};

const providerConnectionDetailOp: Operation<
  { connectionId: string },
  void,
  void,
  ProviderConnectionLifecycleResponse
> = {
  method: "GET",
  path: ({ connectionId }) => `/api/provider-connections/${encodeURIComponent(connectionId)}`,
  params: providerConnectionIdParamsSchema,
  response: providerConnectionLifecycleResponseSchema,
};

const providerConnectionReauthorizationPlanOp: Operation<
  { connectionId: string },
  void,
  ProviderConnectionLifecycleBody,
  ProviderConnectionLifecycleResponse
> = {
  method: "POST",
  path: ({ connectionId }) =>
    `/api/provider-connections/${encodeURIComponent(connectionId)}/reauthorization-plans`,
  params: providerConnectionIdParamsSchema,
  body: providerConnectionLifecycleBodySchema,
  response: providerConnectionLifecycleResponseSchema,
};

const providerConnectionRevocationPlanOp: Operation<
  { connectionId: string },
  void,
  ProviderConnectionLifecycleBody,
  ProviderConnectionLifecycleResponse
> = {
  method: "POST",
  path: ({ connectionId }) =>
    `/api/provider-connections/${encodeURIComponent(connectionId)}/revocation-plans`,
  params: providerConnectionIdParamsSchema,
  body: providerConnectionLifecycleBodySchema,
  response: providerConnectionLifecycleResponseSchema,
};

const providerConnectionInventoryRefreshOp: Operation<
  { connectionId: string },
  void,
  ProviderConnectionLifecycleBody,
  ProviderConnectionLifecycleResponse
> = {
  method: "POST",
  path: ({ connectionId }) =>
    `/api/provider-connections/${encodeURIComponent(connectionId)}/inventory-refresh`,
  params: providerConnectionIdParamsSchema,
  body: providerConnectionLifecycleBodySchema,
  response: providerConnectionLifecycleResponseSchema,
};

const providerConnectionExpiredClaimRefusalOp: Operation<
  { connectionId: string },
  void,
  ProviderConnectionExpiredClaimRefusalBody,
  ProviderConnectionLifecycleResponse
> = {
  method: "POST",
  path: ({ connectionId }) =>
    `/api/provider-connections/${encodeURIComponent(connectionId)}/expired-claim-refusals`,
  params: providerConnectionIdParamsSchema,
  body: providerConnectionExpiredClaimRefusalBodySchema,
  response: providerConnectionLifecycleResponseSchema,
};

export const environments = {
  list: listOp,
  uiContext: uiContextOp,
  viewerChallenge: viewerChallengeOp,
  viewerBinding: viewerBindingOp,
  get: getOp,
  health: healthOp,
  capabilities: capabilitiesOp,
  d1Admission: environmentD1AdmissionOp,
  currentResourceGrantStatus: environmentCurrentResourceGrantStatusOp,
  collectorHostStatus: environmentCollectorHostStatusOp,
  operations: {
    status: operationStatusOp,
    events: operationEventsOp,
    cancel: operationCancelOp,
    resume: operationResumeOp,
    reconcile: operationReconcileOp,
  },
  providerConnections: {
    list: providerConnectionsListOp,
    authorizationPlan: providerConnectionAuthorizationPlanOp,
    executePlan: providerConnectionPlanExecuteOp,
    detail: providerConnectionDetailOp,
    reauthorizationPlan: providerConnectionReauthorizationPlanOp,
    revocationPlan: providerConnectionRevocationPlanOp,
    inventoryRefresh: providerConnectionInventoryRefreshOp,
    expiredClaimRefusal: providerConnectionExpiredClaimRefusalOp,
  },
};
